/**
 * Write-through-кеш прогресса в `localStorage` (ADR-050, FR-20/FR-21).
 *
 * Кеш решает две задачи: убирает мигание тура до ответа `GET /account/onboarding`
 * и хранит очередь неотправленных событий, если `PATCH` не дошёл. Источник правды —
 * всегда сервер; локальные данные только дополняют его при мердже.
 */

import type { ProgressEvent, SeenStep, TourOutcome } from '../types';
import { mergeSeenSteps } from './effectiveSeen';

/** Подмножество Storage, достаточное модулю: позволяет тестировать без jsdom. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface LocalTourState {
  tourId: string;
  campaignId: string;
  manifestVersion: string;
  seenSteps: SeenStep[];
  /** События, не подтверждённые сервером; переотправляются при следующей загрузке. */
  pending: ProgressEvent[];
  tourOutcome?: TourOutcome;
}

const KEY_PREFIX = 'k1.onboarding.';

/** Сервер отвергает батч длиннее 50 событий (правило M-6) — очередь ограничена тем же числом. */
const MAX_PENDING = 50;

export function localStateKey(tourId: string): string {
  return `${KEY_PREFIX}${tourId}`;
}

function defaultStorage(): StorageLike | null {
  try {
    // Приватный режим, заблокированные cookies и запуск вне браузера (тесты движка).
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

export function readLocalState(tourId: string, storage = defaultStorage()): LocalTourState | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(localStateKey(tourId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<LocalTourState>;
    if (parsed.tourId !== tourId) return null;
    return {
      tourId,
      campaignId: parsed.campaignId ?? '',
      manifestVersion: parsed.manifestVersion ?? '0.0.0',
      seenSteps: parsed.seenSteps ?? [],
      pending: parsed.pending ?? [],
      tourOutcome: parsed.tourOutcome,
    };
  } catch {
    // Повреждённый или чужой формат кеша: работаем так, будто его нет.
    return null;
  }
}

function writeLocalState(state: LocalTourState, storage: StorageLike | null): void {
  if (!storage) return;
  try {
    storage.setItem(localStateKey(state.tourId), JSON.stringify(state));
  } catch {
    // Переполненное хранилище не должно ронять тур.
  }
}

export function clearLocalState(tourId: string, storage = defaultStorage()): void {
  if (!storage) return;
  try {
    storage.removeItem(localStateKey(tourId));
  } catch {
    // no-op
  }
}

export interface RecordStepInput {
  tourId: string;
  campaignId: string;
  manifestVersion: string;
  event: ProgressEvent;
  tourOutcome?: TourOutcome;
}

/**
 * Отмечает шаг просмотренным локально и кладёт событие в очередь отправки.
 * Смена кампании обнуляет кеш — по тому же правилу M-3, что и на сервере.
 */
export function recordStepLocally(input: RecordStepInput, storage = defaultStorage()): LocalTourState {
  const previous = readLocalState(input.tourId, storage);
  const carried = previous?.campaignId === input.campaignId ? previous : null;

  const seenStep: SeenStep = {
    id: input.event.stepId,
    rev: input.event.rev,
    at: input.event.at,
    outcome: input.event.outcome,
  };

  const next: LocalTourState = {
    tourId: input.tourId,
    campaignId: input.campaignId,
    manifestVersion: input.manifestVersion,
    seenSteps: mergeSeenSteps(carried?.seenSteps, [seenStep]),
    pending: [...(carried?.pending ?? []), input.event].slice(-MAX_PENDING),
    tourOutcome: input.tourOutcome ?? carried?.tourOutcome,
  };

  writeLocalState(next, storage);
  return next;
}

/** Очищает очередь после успешного `PATCH`, сохраняя отметки просмотра. */
export function clearPending(tourId: string, storage = defaultStorage()): void {
  const state = readLocalState(tourId, storage);
  if (!state) return;
  writeLocalState({ ...state, pending: [] }, storage);
}
